if (!customElements.get('c-opt-product-gallery')) {
  customElements.define('c-opt-product-gallery', class extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.initialize());
    }
    initialize() {
      this.root = this.closest('.c-opt-gallery');
      this.gallery = this.root?.querySelector('media-gallery');
      this.viewer = this.gallery?.querySelector('media-gallery-slider');
      this.list = this.gallery?.querySelector('.thumbnail-list');
      if (!this.viewer) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.counter = document.createElement('span');
      this.counter.className = 'c-opt-gallery-counter';
      this.counter.hidden = true;
      this.counter.setAttribute('aria-hidden', 'true');
      this.viewer.append(this.counter);
      if (this.list) {
        this.controls = document.createElement('div');
        this.controls.className = 'c-opt-thumb-controls';
        this.buttons = [-1, 1].map(direction => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'c-opt-thumb-control';
          button.setAttribute('aria-label', direction < 0 ? this.dataset.previous : this.dataset.next);
          button.setAttribute('aria-controls', this.list.id);
          button.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="' + (direction < 0 ? 'm6 15 6-6 6 6' : 'm6 9 6 6 6-6') + '"/></svg>';
          button.addEventListener('click', () => this.list.scrollBy({ top: direction * this.list.clientHeight * .8, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' }), options);
          this.controls.append(button);
          return button;
        });
        this.list.after(this.controls);
        this.list.addEventListener('scroll', () => this.updateButtons(), { ...options, passive: true });
        this.list.addEventListener('keydown', event => {
          const current = event.target.closest('.thumbnail');
          if (!current) return;
          const items = [...this.list.querySelectorAll('.thumbnail')].filter(button => button.getClientRects().length);
          const index = items.indexOf(current);
          let next;
          if (['ArrowRight', 'ArrowDown'].includes(event.key)) next = items[(index + 1) % items.length];
          if (['ArrowLeft', 'ArrowUp'].includes(event.key)) next = items[(index - 1 + items.length) % items.length];
          if (event.key === 'Home') next = items[0];
          if (event.key === 'End') next = items.at(-1);
          if (next) { event.preventDefault(); next.focus({ preventScroll: true }); next.click(); }
        }, options);
      }
      this.schedule = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.refresh());
      };
      this.mutations = new MutationObserver(this.schedule);
      this.mutations.observe(this.gallery, { subtree: true, attributes: true, attributeFilter: ['aria-current', 'class'] });
      this.resize = new ResizeObserver(this.schedule);
      this.resize.observe(this.viewer);
      this.header = document.querySelector('.lx-ref-header-section');
      if (this.header) this.resize.observe(this.header);
      window.addEventListener('resize', this.schedule, options);
      window.addEventListener('scroll', this.schedule, { ...options, passive: true });
      this.refresh();
    }
    refresh() {
      if (!this.isConnected || !this.viewer) return;
      const imageHeight = Math.round(this.viewer.getBoundingClientRect().height);
      this.root.style.setProperty('--c-opt-image-height', imageHeight + 'px');
      const headerStyle = this.header && getComputedStyle(this.header);
      const headerBottom = headerStyle && ['sticky', 'fixed'].includes(headerStyle.position) ? Math.max(0, this.header.getBoundingClientRect().bottom) : 0;
      const top = headerBottom + 16;
      this.root.style.setProperty('--c-opt-sticky-top', top + 'px');
      this.root.toggleAttribute('data-c-opt-short', this.gallery.getBoundingClientRect().height + top + 16 > window.innerHeight);
      if (!this.list) return;
      const vertical = matchMedia('(min-width: 1200px)').matches;
      const items = [...this.list.querySelectorAll('.thumbnail-list__item')].filter(item => item.getClientRects().length);
      const contentHeight = items.reduce((height, item) => height + item.getBoundingClientRect().height, 0) + Math.max(0, items.length - 1) * 10 + 6;
      this.root.toggleAttribute('data-c-opt-overflow', vertical && contentHeight > imageHeight);
      const active = this.list.querySelector('[aria-current="true"]')?.closest('li');
      if (active && this.lastActive !== active) {
        this.lastActive = active;
        const bounds = this.list.getBoundingClientRect();
        const itemBounds = active.getBoundingClientRect();
        if (vertical) {
          if (itemBounds.top < bounds.top) this.list.scrollTop -= bounds.top - itemBounds.top + 3;
          else if (itemBounds.bottom > bounds.bottom) this.list.scrollTop += itemBounds.bottom - bounds.bottom + 3;
        } else {
          if (itemBounds.left < bounds.left) this.list.scrollLeft -= bounds.left - itemBounds.left + 3;
          else if (itemBounds.right > bounds.right) this.list.scrollLeft += itemBounds.right - bounds.right + 3;
        }
      }
      const index = active ? items.indexOf(active) : 0;
      this.counter.textContent = (index + 1) + ' / ' + items.length;
      this.counter.hidden = items.length <= 1;
      this.updateButtons();
    }
    updateButtons() {
      if (!this.buttons) return;
      this.buttons[0].disabled = this.list.scrollTop <= 2;
      this.buttons[1].disabled = this.list.scrollTop + this.list.clientHeight >= this.list.scrollHeight - 2;
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.abort?.abort();
      this.mutations?.disconnect();
      this.resize?.disconnect();
      this.controls?.remove();
      this.counter?.remove();
    }
  });
}
